import { MathUtils } from './math-utils.js';
import { PERFORMANCE_LIMITS, getColorPattern } from './config.js';
import { CANVAS_CONSTANTS } from './constants.js';
// Export utilities imported but currently unused - kept for future use
// import { calculateTreeBounds, calculateCropDimensions } from './export-utils.js';

/**
 * Viewport transformation manager for zoom/pan operations
 */
export class ViewportTransform {
    constructor() {
        this.offsetX = 0; // Pan offset X
        this.offsetY = 0; // Pan offset Y
        this.scale = 1.0; // Zoom scale
        this.canvasWidth = 0;
        this.canvasHeight = 0;
    }

    /**
     * Set canvas dimensions
     * @param {number} width - Canvas width
     * @param {number} height - Canvas height
     */
    setCanvasSize(width, height) {
        this.canvasWidth = width;
        this.canvasHeight = height;
    }

    /**
     * Convert world coordinates to screen coordinates
     * @param {number} worldX - World X coordinate
     * @param {number} worldY - World Y coordinate
     * @returns {Object} Screen coordinates {x, y}
     */
    worldToScreen(worldX, worldY) {
        return {
            x: (worldX + this.offsetX) * this.scale + this.canvasWidth / 2,
            y: (worldY + this.offsetY) * this.scale + this.canvasHeight / 2,
        };
    }

    /**
     * Convert screen coordinates to world coordinates
     * @param {number} screenX - Screen X coordinate
     * @param {number} screenY - Screen Y coordinate
     * @returns {Object} World coordinates {x, y}
     */
    screenToWorld(screenX, screenY) {
        return {
            x: (screenX - this.canvasWidth / 2) / this.scale - this.offsetX,
            y: (screenY - this.canvasHeight / 2) / this.scale - this.offsetY,
        };
    }

    /**
     * Zoom to specific point while keeping it fixed on screen
     * @param {number} zoomFactor - Zoom multiplier
     * @param {number} screenX - Screen X coordinate to zoom towards
     * @param {number} screenY - Screen Y coordinate to zoom towards
     */
    zoomToPoint(zoomFactor, screenX, screenY) {
        const worldPoint = this.screenToWorld(screenX, screenY);

        this.scale *= zoomFactor;
        this.scale = MathUtils.clamp(this.scale, 0.1, 10.0);

        // Adjust offset to keep world point at same screen position
        const newScreenPoint = this.worldToScreen(worldPoint.x, worldPoint.y);
        this.offsetX += (screenX - newScreenPoint.x) / this.scale;
        this.offsetY += (screenY - newScreenPoint.y) / this.scale;
    }

    /**
     * Pan the viewport by screen pixels
     * @param {number} deltaX - Pan distance X in pixels
     * @param {number} deltaY - Pan distance Y in pixels
     */
    panBy(deltaX, deltaY) {
        this.offsetX += deltaX / this.scale;
        this.offsetY += deltaY / this.scale;

        // Apply pan boundaries
        const maxPan = CANVAS_CONSTANTS.MAX_PAN_DISTANCE;
        this.offsetX = MathUtils.clamp(this.offsetX, -maxPan, maxPan);
        this.offsetY = MathUtils.clamp(this.offsetY, -maxPan, maxPan);
    }

    /**
     * Reset viewport to default state
     */
    resetView() {
        this.offsetX = 0;
        this.offsetY = 0;
        this.scale = 1.0;
    }

    /**
     * Get current viewport state
     * @returns {Object} Viewport state
     */
    getViewport() {
        return {
            offsetX: this.offsetX,
            offsetY: this.offsetY,
            scale: this.scale,
        };
    }

    /**
     * Set viewport state
     * @param {number} x - Offset X
     * @param {number} y - Offset Y
     * @param {number} zoom - Zoom scale
     */
    setViewport(x, y, zoom) {
        this.offsetX = x;
        this.offsetY = y;
        this.scale = MathUtils.clamp(zoom, 0.1, 10.0);
    }
}

/**
 * Canvas renderer for Pythagoras Trees
 */
export class CanvasRenderer {
    /**
     * Create canvas renderer
     * @param {HTMLCanvasElement} canvasElement - Canvas element
     * @param {Object} config - Configuration object
     */
    constructor(canvasElement, config = {}) {
        this.canvas = canvasElement;
        this.ctx = canvasElement.getContext('2d');
        this.config = config;
        this.viewport = new ViewportTransform();

        this.setupCanvas();
        this.bindResizeHandler();
    }

    /**
     * Setup canvas initial state
     */
    setupCanvas() {
        this.resizeCanvas();

        // Enable anti-aliasing if configured
        if (this.config.antiAliasing) {
            this.ctx.imageSmoothingEnabled = true;
            this.ctx.imageSmoothingQuality = 'high';
        }

        // Set initial viewport to show tree growing upward from center
        this.viewport.offsetY = 100; // Move view down to see base square and tree growth
        this.viewport.scale = 0.7; // Zoom out slightly to see more of the tree
    }

    /**
     * Resize canvas to fit container
     */
    resizeCanvas() {
        // Get actual canvas container dimensions instead of window dimensions
        const canvasContainer = this.canvas.parentElement;
        if (!canvasContainer) {
            // Canvas container not found, falling back to window dimensions
            const width = window.innerWidth;
            const height = window.innerHeight;
            this.setCanvasDimensions(width, height);
            return;
        }

        // Use container's actual dimensions, accounting for panel state
        const containerRect = canvasContainer.getBoundingClientRect();
        const width = containerRect.width || window.innerWidth;
        const height = containerRect.height || window.innerHeight;

        // Ensure we have valid dimensions
        if (width <= 0 || height <= 0) {
            // Invalid container dimensions, using fallback
            this.setCanvasDimensions(window.innerWidth, window.innerHeight);
            return;
        }

        this.setCanvasDimensions(width, height);
    }

    /**
     * Set canvas dimensions and update viewport
     * @param {number} width - Canvas width
     * @param {number} height - Canvas height
     */
    setCanvasDimensions(width, height) {
        const dpr = window.devicePixelRatio || 1;

        // Set canvas size with device pixel ratio for crisp rendering
        this.canvas.width = width * dpr;
        this.canvas.height = height * dpr;
        this.canvas.style.width = width + 'px';
        this.canvas.style.height = height + 'px';

        // Scale context to account for device pixel ratio
        this.ctx.scale(dpr, dpr);
        this.viewport.setCanvasSize(width, height);

        // Re-apply anti-aliasing settings after resize
        if (this.config.antiAliasing) {
            this.ctx.imageSmoothingEnabled = true;
            this.ctx.imageSmoothingQuality = 'high';
        }
    }

    /**
     * Bind window resize handler
     */
    bindResizeHandler() {
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                this.resizeCanvas();
                // Trigger redraw if callback is available
                if (this.redrawCallback) {
                    this.redrawCallback();
                }
            }, 100);
        });
    }

    /**
     * Set redraw callback for when canvas needs to be redrawn
     * @param {Function} callback - Callback function to trigger redraw
     */
    setRedrawCallback(callback) {
        this.redrawCallback = callback;
    }

    /**
     * Clear the canvas
     */
    clear() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // Draw background color
        if (this.config.backgroundColor) {
            this.ctx.fillStyle = this.config.backgroundColor;
            this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        }
    } /**
     * Render complete tree with color patterns and performance optimizations
     * @param {Array} squares - Array of Square objects
     * @param {Array} triangles - Array of Triangle objects (optional)
     * @param {Object} config - Rendering configuration
     */
    renderTree(squares, triangles = [], config) {
        this.clear();

        // Get active color pattern
        const pattern = getColorPattern(config.colorPattern) || getColorPattern('classic');

        // Calculate max level without using spread operator (which causes stack overflow with large arrays)
        let maxLevel = 0;
        if (squares.length > 0) {
            for (const square of squares) {
                if (square.level > maxLevel) {
                    maxLevel = square.level;
                }
            }
        }

        let renderedCount = 0;
        const maxRender = PERFORMANCE_LIMITS.MAX_VISIBLE_SQUARES;

        // Performance optimization: Sort by level (smallest to largest)
        // This ensures that the base levels are always rendered, even within the render limit
        // Use a stable, in-place sort that won't cause stack overflow with large arrays
        const sortedSquares = [...squares];
        sortedSquares.sort((a, b) => a.level - b.level);

        // Render squares if pattern shows them
        if (pattern.showSquares && pattern.renderSquares) {
            // Iterative approach (non-recursive) to avoid stack overflow
            for (const square of sortedSquares) {
                if (renderedCount >= maxRender) {
                    break;
                }

                // Skip rendering if the square is too small or outside viewport
                if (this.shouldRenderSquare(square)) {
                    // Use try-catch to prevent one rendering error from crashing the entire tree
                    try {
                        const color = pattern.renderSquares(
                            square,
                            config,
                            maxLevel,
                            squares,
                            triangles
                        );
                        if (color) {
                            if (typeof color === 'object' && color.stroke) {
                                if (color.edges && Array.isArray(color.edges)) {
                                    // Selective edge drawing mode (for outline picture)
                                    this.drawSquareEdges(
                                        square,
                                        color.stroke,
                                        color.lineWidth || 1,
                                        color.edges
                                    );
                                } else {
                                    // Full outline mode
                                    this.drawSquareOutline(
                                        square,
                                        color.stroke,
                                        color.lineWidth || 1
                                    );
                                }
                            } else {
                                // Fill mode
                                const strokeColor = config.drawBorder ? '#000000' : null;
                                this.drawSquare(square, color, strokeColor);
                            }
                        }
                        renderedCount++;
                    } catch (err) {
                        // Error rendering square at level ${square.level}
                        // Continue with next square instead of crashing
                    }
                }
            }
        }

        // Render triangles if pattern shows them and triangles are provided
        if (pattern.showTriangles && pattern.renderTriangles && triangles.length > 0) {
            // Sort triangles by level for consistent rendering priority
            const sortedTriangles = [...triangles];
            sortedTriangles.sort((a, b) => a.level - b.level);

            // Calculate max triangle level without using spread operator
            let maxTriangleLevel = 0;
            for (const triangle of triangles) {
                if (triangle.level > maxTriangleLevel) {
                    maxTriangleLevel = triangle.level;
                }
            }

            for (const triangle of sortedTriangles) {
                // Skip rendering if triangle is too small or outside viewport
                if (this.shouldRenderTriangle(triangle)) {
                    try {
                        const color = pattern.renderTriangles(
                            triangle,
                            config,
                            maxLevel,
                            squares,
                            triangles
                        );
                        if (color) {
                            if (typeof color === 'object' && color.stroke) {
                                // Outline mode
                                this.drawTriangleOutline(
                                    triangle,
                                    color.stroke,
                                    color.lineWidth || 1
                                );
                            } else {
                                // Fill mode
                                const strokeColor = config.drawBorder ? '#000000' : null;
                                this.drawTriangle(triangle, color, strokeColor);
                            }
                        }
                    } catch (err) {
                        // Error rendering triangle at level ${triangle.level}
                        // Continue with next triangle instead of crashing
                    }
                }
            }
        }
    }

    /**
     * Check if square should be rendered (performance optimization)
     * @param {Square} square - Square to check
     * @returns {boolean} True if should render
     */
    shouldRenderSquare(square) {
        // Skip squares that are too small to see
        const screenSize = square.size * this.viewport.scale;
        if (screenSize < PERFORMANCE_LIMITS.MIN_SQUARE_SIZE_PIXELS) {
            return false;
        }

        // Skip squares outside viewport (with margin)
        const screenPos = this.viewport.worldToScreen(square.x, square.y);
        const margin = screenSize;

        return (
            screenPos.x + margin >= 0 &&
            screenPos.x - margin <= this.viewport.canvasWidth &&
            screenPos.y + margin >= 0 &&
            screenPos.y - margin <= this.viewport.canvasHeight
        );
    }

    /**
     * Check if triangle should be rendered (performance optimization)
     * @param {Triangle} triangle - Triangle to check
     * @returns {boolean} True if should render
     */
    shouldRenderTriangle(triangle) {
        // Calculate triangle bounds (simplified - use largest vertex distance)
        const vertices = triangle.vertices;
        if (!vertices || vertices.length < 3) {
            return false;
        }

        let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
        for (const vertex of vertices) {
            const screenPos = this.viewport.worldToScreen(vertex.x, vertex.y);
            minX = Math.min(minX, screenPos.x);
            minY = Math.min(minY, screenPos.y);
            maxX = Math.max(maxX, screenPos.x);
            maxY = Math.max(maxY, screenPos.y);
        }

        // Check if triangle is within viewport bounds
        const margin = 50; // Extra margin for triangles
        return (
            maxX + margin >= 0 &&
            minX - margin <= this.viewport.canvasWidth &&
            maxY + margin >= 0 &&
            minY - margin <= this.viewport.canvasHeight
        );
    }

    /**
     * Get color for square based on level (legacy method for backward compatibility)
     * @param {Square} square - Square object
     * @param {Object} config - Configuration with colors
     * @returns {string} Color string
     */
    getSquareColor(square, config) {
        // Use classic pattern logic for backward compatibility
        return square.level % 2 === 0
            ? config.color1 || config.evenColor
            : config.color2 || config.oddColor;
    }

    /**
     * Draw a single square with transformations
     * @param {Square} square - Square to draw
     * @param {string} color - Fill color
     * @param {string} strokeColor - Stroke color (optional)
     */
    drawSquare(square, color, strokeColor = null) {
        this.ctx.save();

        // Apply viewport transformation
        const screenPos = this.viewport.worldToScreen(square.x, square.y);
        const screenSize = square.size * this.viewport.scale;

        // Move to square center and apply rotation
        this.ctx.translate(screenPos.x, screenPos.y);
        this.ctx.rotate(square.angle);

        // Draw square centered at origin
        const halfSize = screenSize / 2;

        if (color) {
            this.ctx.fillStyle = color;
            this.ctx.fillRect(-halfSize, -halfSize, screenSize, screenSize);
        }

        // TEMPORARY: Always draw yellow border for debugging
        //this.ctx.strokeStyle = '#FFFF00'; // Bright yellow
        //this.ctx.lineWidth = 2;
        // this.ctx.strokeRect(-halfSize, -halfSize, screenSize, screenSize);

        // Original stroke color (if specified)
        if (strokeColor && strokeColor !== '#FFFF00') {
            this.ctx.strokeStyle = strokeColor;
            this.ctx.lineWidth = 1;
            this.ctx.strokeRect(-halfSize, -halfSize, screenSize, screenSize);
        }

        this.ctx.restore();
    }

    /**
     * Draw a square outline only (for outline pattern)
     * @param {Square} square - Square to draw
     * @param {string} strokeColor - Stroke color
     * @param {number} lineWidth - Line width
     */
    drawSquareOutline(square, strokeColor, lineWidth = 1) {
        this.ctx.save();

        // Apply viewport transformation
        const screenPos = this.viewport.worldToScreen(square.x, square.y);
        const screenSize = square.size * this.viewport.scale;

        // Move to square center and apply rotation
        this.ctx.translate(screenPos.x, screenPos.y);
        this.ctx.rotate(square.angle);

        // Draw square outline centered at origin
        const halfSize = screenSize / 2;

        this.ctx.strokeStyle = strokeColor;
        this.ctx.lineWidth = lineWidth;
        this.ctx.strokeRect(-halfSize, -halfSize, screenSize, screenSize);

        this.ctx.restore();
    } /**
     * Draw specific edges of a square (for outline picture pattern)
     * @param {Square} square - Square to draw
     * @param {string} strokeColor - Stroke color
     * @param {number} lineWidth - Line width
     * @param {Array} edges - Array of edge names to draw: ['top', 'right', 'bottom', 'left']
     */
    drawSquareEdges(
        square,
        strokeColor,
        lineWidth = 1,
        edges = ['top', 'right', 'bottom', 'left']
    ) {
        this.ctx.save();

        // Apply viewport transformation
        const screenPos = this.viewport.worldToScreen(square.x, square.y);
        const screenSize = square.size * this.viewport.scale;

        // Move to square center and apply rotation
        this.ctx.translate(screenPos.x, screenPos.y);
        this.ctx.rotate(square.angle);

        this.ctx.strokeStyle = strokeColor;
        this.ctx.lineWidth = lineWidth;

        const halfSize = screenSize / 2;

        // Define the four corners of the square (centered at origin)
        const corners = {
            topLeft: { x: -halfSize, y: -halfSize },
            topRight: { x: halfSize, y: -halfSize },
            bottomRight: { x: halfSize, y: halfSize },
            bottomLeft: { x: -halfSize, y: halfSize },
        };

        // Draw only the specified edges
        this.ctx.beginPath();

        edges.forEach(edge => {
            switch (edge) {
                case 'top':
                    this.ctx.moveTo(corners.topLeft.x, corners.topLeft.y);
                    this.ctx.lineTo(corners.topRight.x, corners.topRight.y);
                    break;
                case 'right':
                    this.ctx.moveTo(corners.topRight.x, corners.topRight.y);
                    this.ctx.lineTo(corners.bottomRight.x, corners.bottomRight.y);
                    break;
                case 'bottom':
                    this.ctx.moveTo(corners.bottomRight.x, corners.bottomRight.y);
                    this.ctx.lineTo(corners.bottomLeft.x, corners.bottomLeft.y);
                    break;
                case 'left':
                    this.ctx.moveTo(corners.bottomLeft.x, corners.bottomLeft.y);
                    this.ctx.lineTo(corners.topLeft.x, corners.topLeft.y);
                    break;
            }
        });

        this.ctx.stroke();
        this.ctx.restore();
    }

    /**
     * Draw a triangle with specified color
     * @param {Triangle} triangle - Triangle to draw
     * @param {string} color - Fill color
     */
    drawTriangle(triangle, color, strokeColor = null) {
        if (!triangle.vertices || triangle.vertices.length < 3) {
            return;
        }

        this.ctx.save();
        this.ctx.fillStyle = color || 'rgba(255, 255, 0, 0.3)'; // Default semi-transparent yellow

        this.ctx.beginPath();

        triangle.vertices.forEach((vertex, index) => {
            const screenPos = this.viewport.worldToScreen(vertex.x, vertex.y);
            if (index === 0) {
                this.ctx.moveTo(screenPos.x, screenPos.y);
            } else {
                this.ctx.lineTo(screenPos.x, screenPos.y);
            }
        });

        this.ctx.closePath();
        this.ctx.fill();

        // Add black border if specified
        if (strokeColor) {
            this.ctx.strokeStyle = strokeColor;
            this.ctx.lineWidth = 1;
            this.ctx.stroke();
        }

        this.ctx.restore();
    }

    /**
     * Draw triangle outline with specified stroke color and line width
     * @param {Triangle} triangle - Triangle to draw
     * @param {string} strokeColor - Stroke color
     * @param {number} lineWidth - Line width
     */
    drawTriangleOutline(triangle, strokeColor, lineWidth = 1) {
        if (!triangle.vertices || triangle.vertices.length < 3) {
            return;
        }

        this.ctx.save();
        this.ctx.strokeStyle = strokeColor;
        this.ctx.lineWidth = lineWidth;

        this.ctx.beginPath();

        triangle.vertices.forEach((vertex, index) => {
            const screenPos = this.viewport.worldToScreen(vertex.x, vertex.y);
            if (index === 0) {
                this.ctx.moveTo(screenPos.x, screenPos.y);
            } else {
                this.ctx.lineTo(screenPos.x, screenPos.y);
            }
        });

        this.ctx.closePath();
        this.ctx.stroke();

        this.ctx.restore();
    }

    /**
     * Draw level numbers on squares (debug feature)
     * @param {Square} square - Square to label
     */
    drawLevelNumber(square) {
        if (!this.config.showLevelNumbers) {
            return;
        }

        const screenPos = this.viewport.worldToScreen(square.x, square.y);
        const fontSize = Math.max(8, square.size * this.viewport.scale * 0.3);

        this.ctx.save();
        this.ctx.fillStyle = '#000000';
        this.ctx.font = `${fontSize}px Arial`;
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(square.level.toString(), screenPos.x, screenPos.y);
        this.ctx.restore();
    }

    /**
     * Get viewport transform object
     * @returns {ViewportTransform} Viewport transform
     */
    getViewportTransform() {
        return this.viewport;
    } /**
     * Export current canvas as data URL or SVG content
     * @param {string} format - Image format ('png', 'jpeg', 'svg')
     * @param {number} quality - Image quality (0-1) for raster formats
     * @param {Object} options - Export options for SVG
     * @returns {string} Data URL for raster formats, SVG content for SVG format
     */
    exportCanvas(format = 'png', quality = 1.0) {
        if (format === 'svg') {
            // For SVG export, we need access to the original tree data
            // This method should be called from the main app which has access to the tree data
            throw new Error(
                'SVG export requires tree data. Use main application exportSVG method instead.'
            );
        }

        return this.canvas.toDataURL(`image/${format}`, quality);
    }

    /**
     * Export cropped canvas with automatic bounds detection
     * @param {Array} squares - Array of Square objects for bounds calculation
     * @param {Array} triangles - Array of Triangle objects for bounds calculation
     * @param {string} format - Image format ('png', 'jpeg')
     * @param {number} quality - Image quality (0-1)
     * @param {Object} options - Export options
     * @returns {string} Data URL of cropped image
     */
    exportCanvasCropped(squares, triangles = [], format = 'png', quality = 1.0, options = {}) {
        const opts = {
            padding: 50, // Standard padding around tree
            resolution: 2, // 2x resolution for high quality
            minSize: 400, // Minimum export size
            ...options,
        };

        // Handle empty tree case
        if (squares.length === 0) {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = opts.minSize;
            tempCanvas.height = opts.minSize;
            const tempCtx = tempCanvas.getContext('2d');
            tempCtx.fillStyle = this.config?.backgroundColor || '#ffffff';
            tempCtx.fillRect(0, 0, opts.minSize, opts.minSize);
            return tempCanvas.toDataURL(`image/${format}`, quality);
        }

        // Find the actual pixel bounds of content on the canvas by analyzing rendered pixels
        const canvasBounds = this.findContentBounds();

        // Calculate export dimensions with padding
        const contentWidth = canvasBounds.maxX - canvasBounds.minX;
        const contentHeight = canvasBounds.maxY - canvasBounds.minY;
        const exportWidth = Math.max(contentWidth + opts.padding * 2, opts.minSize);
        const exportHeight = Math.max(contentHeight + opts.padding * 2, opts.minSize);

        // Create export canvas
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = exportWidth * opts.resolution;
        tempCanvas.height = exportHeight * opts.resolution;

        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.imageSmoothingEnabled = true;
        tempCtx.imageSmoothingQuality = 'high';

        // Fill with background color
        tempCtx.fillStyle = this.config?.backgroundColor || '#ffffff';
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

        // Scale for high resolution
        tempCtx.scale(opts.resolution, opts.resolution);

        // Calculate where to place the content (centered with padding)
        const offsetX = (exportWidth - contentWidth) / 2 - canvasBounds.minX;
        const offsetY = (exportHeight - contentHeight) / 2 - canvasBounds.minY;

        // Draw the original canvas content, positioned to center the tree
        tempCtx.drawImage(this.canvas, offsetX, offsetY);

        return tempCanvas.toDataURL(`image/${format}`, quality);
    }

    /**
     * Find the actual pixel bounds of non-background content on canvas
     * @returns {Object} Pixel bounds {minX, minY, maxX, maxY}
     */
    findContentBounds() {
        const ctx = this.ctx;
        const imageData = ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
        const pixels = imageData.data;

        // Get background color RGB (default to white if not set)
        const bgColor = this.config?.backgroundColor || '#ffffff';
        const bgRgb = this.hexToRgb(bgColor);

        let minX = this.canvas.width,
            minY = this.canvas.height;
        let maxX = 0,
            maxY = 0;
        let foundContent = false;

        // Scan pixels to find non-background content
        for (let y = 0; y < this.canvas.height; y++) {
            for (let x = 0; x < this.canvas.width; x++) {
                const index = (y * this.canvas.width + x) * 4;
                const r = pixels[index];
                const g = pixels[index + 1];
                const b = pixels[index + 2];
                const a = pixels[index + 3];

                // Check if pixel is not background color (with some tolerance for antialiasing)
                if (a > 0 && !this.isBackgroundColor(r, g, b, bgRgb)) {
                    foundContent = true;
                    minX = Math.min(minX, x);
                    minY = Math.min(minY, y);
                    maxX = Math.max(maxX, x);
                    maxY = Math.max(maxY, y);
                }
            }
        }

        // Fallback if no content found
        if (!foundContent) {
            return { minX: 0, minY: 0, maxX: this.canvas.width, maxY: this.canvas.height };
        }

        return { minX, minY, maxX, maxY };
    }

    /**
     * Convert hex color to RGB
     * @param {string} hex - Hex color string
     * @returns {Object} RGB values
     */
    hexToRgb(hex) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
            ? {
                  r: parseInt(result[1], 16),
                  g: parseInt(result[2], 16),
                  b: parseInt(result[3], 16),
              }
            : { r: 255, g: 255, b: 255 }; // default to white
    }

    /**
     * Check if a pixel color matches background (with tolerance)
     * @param {number} r - Red value
     * @param {number} g - Green value
     * @param {number} b - Blue value
     * @param {Object} bgRgb - Background RGB
     * @returns {boolean} True if matches background
     */
    isBackgroundColor(r, g, b, bgRgb) {
        const tolerance = 10; // Allow some tolerance for antialiasing
        return (
            Math.abs(r - bgRgb.r) <= tolerance &&
            Math.abs(g - bgRgb.g) <= tolerance &&
            Math.abs(b - bgRgb.b) <= tolerance
        );
    }

    /**
     * Transform world coordinate bounds to canvas pixel bounds
     * @param {Object} worldBounds - Bounds in world coordinates
     * @returns {Object} Bounds in canvas coordinates
     */
    transformBoundsToCanvas(worldBounds) {
        // Transform world coordinates to screen coordinates using viewport transformation
        const topLeft = this.viewport.worldToScreen(worldBounds.minX, worldBounds.minY);
        const bottomRight = this.viewport.worldToScreen(worldBounds.maxX, worldBounds.maxY);

        return {
            minX: topLeft.x,
            minY: topLeft.y,
            maxX: bottomRight.x,
            maxY: bottomRight.y,
            width: bottomRight.x - topLeft.x,
            height: bottomRight.y - topLeft.y,
        };
    }
}
